import { CardElement } from '@stripe/react-stripe-js';
import countryOptions from 'vendor/constants/select-options/countries';
import CheckoutFormStyled from './CheckoutForm.styled';
import { useTranslation } from 'react-i18next';
import { Dropdown, IconButton, SolidButton, ToggleSwitch } from 'dbi-pearl-ui-kit';
import credit from 'assets/credit-cards-group.png';
import { TextInput } from 'dbi-pearl-ui-kit';
import styled from 'styled-components';
import { PearlError } from 'framework';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { StripeService } from 'lib/apis/StripeServiceNew';
import { usePromoCode } from './PromoCodeContext';
import { useActivePromotion } from 'shared/hooks/useActivePromotion';
import { formatPercent } from 'shared/utils/promotionPricing';
const CheckoutElementContainer = styled.div`
    display: flex;
    width: 432px;
    flex-direction: column;
    gap: 16px;
    flex-shrink: 0;
    @media (max-width: 815px) {
        width: 100%;
    }
`;

const CheckoutLabel = styled.p`
    font-family: Objektiv Mk2 Bold;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    line-height: 15px; /* 136.364% */
    letter-spacing: 1.65px;
    text-transform: uppercase;
    margin: 0px;
    color: ${(props) =>
        props.$pearlConnect ? 'var(--Accent, #4a8280)' : props?.theme?.colors?.primary?.base};
`;

// billingFrequency: 'year' or 'month', the frequency picked on the checkout page.
// skipPromotion: Signature Collection vendors never get the automatic sale; a typed code still works.
export default function CheckoutForm({
    pearlConnectChrome = false,
    billingFrequency = 'year',
    skipPromotion = false,
}) {
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState('');
    const { setPromoCodeDiscount } = usePromoCode();
    const [showPromoCode, setShowPromoCode] = useState(false);
    const [promoCodeText, setPromoCodeText] = useState(false);
    const [couponDiscount, setCouponDiscount] = useState(null);
    const [coupon, setCoupon] = useState('');
    const { getValues, watch, setValue, setError, trigger, clearErrors } = useFormContext();
    const { PromoCode } = getValues();
    const promoCodeVal = watch('PromoCode');
    const stripeService = StripeService();
    // A live promotion for this frequency is applied by the backend, so there is no code to enter.
    const { discountPercent: promotionPercent, isLoaded: isPromotionLoaded } = useActivePromotion(
        billingFrequency,
        { disabled: skipPromotion }
    );
    const hasPromotion = promotionPercent > 0;
    const handleToggle = (checked) => {
        setShowPromoCode(checked);
        setPromoCodeText(false);
        clearErrors('PromoCode', '');
    };

    useEffect(() => {
        if (!showPromoCode) {
            setValue('PromoCode', '');
            setPromoCodeText(false);
            setPromoCodeDiscount(null);
        }
    }, [showPromoCode, setValue]);

    useEffect(() => {
        if (hasPromotion) {
            setShowPromoCode(false);
        }
    }, [hasPromotion]);

    useEffect(() => {
        if (promoCodeVal) {
            trigger('PromoCode');
        }
    }, [promoCodeVal]);

    const handleApply = async () => {
        if (!PromoCode) {
            return;
        }
        try {
            const res = await stripeService.getPromocode(PromoCode?.trim());
            const promoCodeDiscount = res.data.CouponCodeDetails.percent_off;
            setCoupon(PromoCode);
            setPromoCodeText(true);
            setCouponDiscount(promoCodeDiscount);
            setPromoCodeDiscount(promoCodeDiscount);
        } catch (error) {
            setError('PromoCode', {
                type: 'manual',
                message: 'Please enter a valid promo code',
            });
            setPromoCodeText(false);
            setPromoCodeDiscount(null);
        }
    };
    const handleClose = () => {
        setPromoCodeText(false);
        setValue('PromoCode', '');
        setPromoCodeDiscount('');
    };

    return (
        <CheckoutFormStyled className={pearlConnectChrome ? 'pearl-connect-checkout' : undefined}>
            <div className="cardContainer">
                <CheckoutElementContainer>
                    {pearlConnectChrome ? (
                        <div className="pearl-name-row">
                            <p className="pearl-field-label">
                                {t(`Forms.StripeCheckout.CardholderNameLabel`)}
                            </p>
                            <TextInput
                                formFieldName="CardholderName"
                                className="card_input"
                                id="cardholder-name"
                            ></TextInput>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <CheckoutLabel $pearlConnect={false}>
                                    {t(`Forms.StripeCheckout.CardholderName`)}
                                </CheckoutLabel>
                                <div className="imageWrapper">
                                    <img alt="credit-cards" src={credit} width="131px"></img>
                                </div>
                            </div>
                            <TextInput
                                formFieldName="CardholderName"
                                className="card_input"
                                id="cardholder-name"
                            ></TextInput>
                        </>
                    )}
                    <PearlError name="CardholderName" />
                </CheckoutElementContainer>
                <CheckoutElementContainer data-testid="stripe-card">
                    {pearlConnectChrome ? (
                        <p className="pearl-field-label">
                            {t(`Forms.StripeCheckout.CardNumberLabel`)}
                        </p>
                    ) : null}
                    <div className={pearlConnectChrome ? 'pearl-card-number-row' : undefined}>
                        {pearlConnectChrome ? (
                            <img
                                className="pearl-card-icon"
                                alt=""
                                src={credit}
                                width={36}
                                height={24}
                            />
                        ) : null}
                        <div className={pearlConnectChrome ? 'pearl-card-element-wrap' : undefined}>
                            <CardElement
                                className="card"
                                options={{
                                    style: {
                                        base: {
                                            '::placeholder': {
                                                color: 'rgb(140, 140, 140)',
                                                fontFamily: 'Poppins',
                                                fontSize: '13px',
                                                fontWeight: 300,
                                            },
                                        },
                                    },
                                }}
                                onChange={(event) => {
                                    if (event.error) {
                                        setErrorMessage(event.error.message);
                                    } else {
                                        setErrorMessage('');
                                    }
                                }}
                            ></CardElement>
                        </div>
                    </div>
                    {errorMessage && <div className="error-message">{errorMessage}</div>}
                </CheckoutElementContainer>
                <CheckoutElementContainer>
                    <Dropdown
                        formFieldName="CountryRegion"
                        id="CountryRegion"
                        providedLabel={
                            pearlConnectChrome
                                ? t('Forms.StripeCheckout.SelectCountryOrRegion')
                                : 'Select country or region'
                        }
                    >
                        {countryOptions.map((country) => (
                            <option key={country.value} value={country.value}>
                                {country.label}
                            </option>
                        ))}
                    </Dropdown>
                    <PearlError name="CountryRegion" />
                </CheckoutElementContainer>
                {isPromotionLoaded && hasPromotion ? (
                    <p className="discountText" data-testid="promotionApplied">
                        {t('Forms.StripeCheckout.PromotionApplied', {
                            percent: formatPercent(promotionPercent),
                        })}
                    </p>
                ) : null}
                {isPromotionLoaded && !hasPromotion ? (
                    <PromoCodeEntry
                        t={t}
                        pearlConnectChrome={pearlConnectChrome}
                        showPromoCode={showPromoCode}
                        onToggle={handleToggle}
                        onApply={handleApply}
                        promoCodeText={promoCodeText}
                        coupon={coupon}
                        couponDiscount={couponDiscount}
                        onClose={handleClose}
                    />
                ) : null}
            </div>
        </CheckoutFormStyled>
    );
}

function PromoCodeEntry({
    t,
    pearlConnectChrome,
    showPromoCode,
    onToggle,
    onApply,
    promoCodeText,
    coupon,
    couponDiscount,
    onClose,
}) {
    return (
        <>
            <div className="toggleSection">
                <ToggleSwitch
                    className="toggleStyles"
                    onChange={onToggle}
                    checked={showPromoCode}
                />
                <p
                    className={pearlConnectChrome ? 'pearl-connect-subheading-accent' : undefined}
                    style={{ marginTop: '7px' }}
                >
                    {t(`Forms.StripeCheckout.PromoCodeLabel`)}
                </p>
            </div>
            {showPromoCode && (
                <div className="promoCodeWithApply">
                    <div className="promoCodeInputWidth">
                        <TextInput formFieldName="PromoCode" />
                        <PearlError name="PromoCode" />
                    </div>
                    <SolidButton onClick={onApply} className="applyStyle">
                        Apply
                    </SolidButton>
                </div>
            )}
            {promoCodeText && (
                <div className="promoCodeTextSection">
                    <div className="promoCodeStyle">
                        <p className="promoCodeText">{coupon}</p>
                        <div className="closeIconStyle">
                            <IconButton
                                iconName="close"
                                size="xs"
                                withBorder={false}
                                clickHandler={onClose}
                            />
                        </div>
                    </div>
                    <p className="discountText">
                        {couponDiscount}
                        {t(`Forms.StripeCheckout.PercentOff`)}
                    </p>
                </div>
            )}
        </>
    );
}
